import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Role } from '../domain/types';
import { authEventAction, normalizeEmail, passwordValidationError } from './authRules';

type Profile = { email: string; role: Role };

interface AuthState {
  user: User | null;
  email: string;
  role: Role | null;
  loading: boolean;
  error: string;
  notice: string;
  recoveryMode: boolean;
  mustChangePassword: boolean;
  passwordLogin: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string, confirmation: string) => Promise<void>;
  setInitialPassword: (password: string, confirmation: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState>(null as never);
export const useAuth = () => useContext(Ctx);

async function fetchProfile(email: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('users')
    .select('email, role')
    .eq('email', email)
    .maybeSingle();
  if (error) throw error;
  return data as Profile | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // Id del usuario ya resuelto (o en curso de resolución). Permite distinguir un
  // login/logout real de los SIGNED_IN/TOKEN_REFRESHED que supabase-js re-emite
  // para el mismo usuario al volver a la pestaña o renovar el token.
  const resolvedUserId = useRef<string | null>(null);

  const resolve = async (sessionUser: User | null) => {
    resolvedUserId.current = sessionUser?.id ?? null;
    if (!sessionUser) {
      setUser(null);
      setEmail('');
      setRole(null);
      setError('');
      setMustChangePassword(false);
      setLoading(false);
      return;
    }

    const em = (sessionUser.email || '').toLowerCase();
    setUser(sessionUser);
    setEmail(em);
    // Marca de primer ingreso: se fija al crear la cuenta y obliga a definir contraseña.
    setMustChangePassword(sessionUser.user_metadata?.must_change_password === true);

    try {
      normalizeEmail(em);
    } catch (err: any) {
      setRole(null);
      setError(err?.message || 'La cuenta no está autorizada.');
      setLoading(false);
      return;
    }

    try {
      const profile = await fetchProfile(em);

      if (!profile) {
        setRole(null);
        setError(`La cuenta ${em} no está autorizada.`);
      } else {
        setRole(profile.role);
        setError('');
      }
    } catch (err: any) {
      setRole(null);
      setError(err?.message || `La cuenta ${em} no está autorizada.`);
    }
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => resolve(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      const next = session?.user ?? null;
      if (authEventAction(next?.id ?? null, resolvedUserId.current) === 'silent') {
        // Mismo usuario: refrescar el objeto user (metadata) sin pasar por
        // `loading`, que desmontaría toda la app y borraría el estado de las páginas.
        if (next) {
          setUser(next);
          setMustChangePassword(next.user_metadata?.must_change_password === true);
        }
        return;
      }
      setLoading(true);
      void resolve(next);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const passwordLogin = async (em: string, pw: string) => {
    setError('');
    setNotice('');
    let normalized = '';
    try { normalized = normalizeEmail(em); } catch (err: any) { setError(err.message); return; }
    const { error: e } = await supabase.auth.signInWithPassword({ email: normalized, password: pw });
    if (e) setError('Email o contraseña incorrectos.');
  };

  const requestPasswordReset = async (em: string) => {
    setError('');
    setNotice('');
    let normalized = '';
    try { normalized = normalizeEmail(em); } catch (err: any) { setError(err.message); return; }
    const { error: e } = await supabase.auth.resetPasswordForEmail(normalized, {
      redirectTo: window.location.origin,
    });
    if (e) {
      setError('No pudimos enviar el enlace. Esperá unos minutos e intentá nuevamente.');
      return;
    }
    setNotice('Si el email está habilitado, recibirá un enlace para cambiar la contraseña.');
  };

  const updatePassword = async (password: string, confirmation: string) => {
    setError('');
    const validationError = passwordValidationError(password, confirmation);
    if (validationError) { setError(validationError); return; }
    const { error: e } = await supabase.auth.updateUser({ password });
    if (e) { setError('No pudimos actualizar la contraseña. Solicitá un enlace nuevo.'); return; }
    setRecoveryMode(false);
    await supabase.auth.signOut();
    setNotice('Contraseña actualizada. Ya podés iniciar sesión.');
  };

  // Primer ingreso obligatorio: fija la contraseña propia y limpia la marca, sin cerrar sesión.
  const setInitialPassword = async (password: string, confirmation: string) => {
    setError('');
    const validationError = passwordValidationError(password, confirmation);
    if (validationError) { setError(validationError); return; }
    const { error: e } = await supabase.auth.updateUser({ password, data: { must_change_password: false } });
    if (e) { setError('No pudimos actualizar la contraseña. Intentá nuevamente.'); return; }
    setMustChangePassword(false);
    setNotice('');
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setEmail('');
    setRole(null);
    setError('');
    setRecoveryMode(false);
    setMustChangePassword(false);
  };

  return (
    <Ctx.Provider value={{ user, email, role, loading, error, notice, recoveryMode, mustChangePassword, passwordLogin, requestPasswordReset, updatePassword, setInitialPassword, logout }}>
      {children}
    </Ctx.Provider>
  );
}
